import {
  AnalysisMode,
  DetonationProfile,
  GhostrModule,
  IcapModule,
  Level,
  NetworkSandboxingModule,
  RemediationAction,
  SandboxManagerModule,
} from '@nirvana/shared/dtos/types/policies/common';
import {
  SandboxEndpointSensorSearchableKeywords,
  SandboxIcapSensorSearchableKeywords,
  SandboxManagerSearchableKeywords,
  SandboxNetworkSensorSearchableKeywords,
} from './sandbox.types';
import { SearchableKeyword } from '../../../../../../ui/gzSearchableAccordionsPanel/types';
import { translateFn } from '../../../../../../utils/translator.helper';

export const NETWORK_SANDBOXING_DEFAULT_VALUE: NetworkSandboxingModule = {
  enabled: false,
  analysisMode: AnalysisMode.MONITORING,
  remediationActions: {
    defaultAction: RemediationAction.REPORT_ONLY,
  },
  settings: {
    isCloudSandbox: true,
    ip: '',
    hostname: '',
    endpointId: null,
  },
  proxy: {
    enabled: false,
    settings: {
      server: 'http://proxy',
      port: 12,
      username: '',
      password: '',
    },
  },
  contentPrefiltering: {
    categories: {
      applications: {
        enabled: true,
        level: Level.MEDIUM,
      },
      documents: {
        enabled: true,
        level: Level.MEDIUM,
      },
      scripts: {
        enabled: true,
        level: Level.MEDIUM,
      },
      archives: {
        enabled: true,
        level: Level.MEDIUM,
      },
      emails: {
        enabled: true,
        level: Level.MEDIUM,
      },
    },
    excludeExtensions: [],
    sizeFilter: {
      enabled: false,
      fileMinSizeKB: 100,
      fileMaxSizeMB: 3,
    },
  },
};
export function getNetworkSandboxingDefaultValue(): NetworkSandboxingModule {
  const defaultValue = NETWORK_SANDBOXING_DEFAULT_VALUE;

  if (!Application.Main.product.isCloud()) {
    defaultValue.settings.detonationProfile = DetonationProfile.MEDIUM;
  }

  return defaultValue;
}

export const GHOSTR_DEFAULT_VALUE: GhostrModule = {
  enabled: false,
  sandboxAnalyzerSettings: {
    ip: '',
    hostname: '',
    fqdn: '',
    endpointId: null,
    detonationProfile: DetonationProfile.MEDIUM,
  },
  proxy: {
    enabled: false,
    settings: {
      server: 'http://proxy',
      port: 12,
      username: '',
      password: '',
    },
  },
  contentPrefiltering: {
    categories: {
      applications: {
        enabled: true,
        level: Level.MEDIUM,
      },
      documents: {
        enabled: true,
        level: Level.MEDIUM,
      },
      scripts: {
        enabled: true,
        level: Level.MEDIUM,
      },
      archives: {
        enabled: true,
        level: Level.MEDIUM,
      },
      emails: {
        enabled: true,
        level: Level.MEDIUM,
      },
    },
    excludeExtensions: [],
    sizeFilter: {
      enabled: false,
      fileMinSizeKB: 100,
      fileMaxSizeMB: 3,
    },
  },
};

export const ICAP_DEFAULT_VALUE: IcapModule = {
  enabled: false,
  sandboxAnalyzerSettings: {
    ip: '',
    hostname: '',
    fqdn: '',
    endpointId: null,
    detonationProfile: DetonationProfile.MEDIUM,
  },
  proxy: {
    enabled: false,
    settings: {
      server: 'http://proxy',
      port: 12,
      username: '',
      password: '',
    },
  },
  contentPrefiltering: {
    categories: {
      applications: {
        enabled: true,
        level: Level.MEDIUM,
      },
      documents: {
        enabled: true,
        level: Level.MEDIUM,
      },
      scripts: {
        enabled: true,
        level: Level.MEDIUM,
      },
      archives: {
        enabled: true,
        level: Level.MEDIUM,
      },
      emails: {
        enabled: true,
        level: Level.MEDIUM,
      },
    },
    excludeExtensions: [],
    sizeFilter: {
      enabled: false,
      fileMinSizeKB: 100,
      fileMaxSizeMB: 3,
    },
    submitFilesMarkedAsMalware: false,
  },
};

export const SANDBOX_MANAGER_DEFAULT_VALUE: SandboxManagerModule = {
  dataRetention: 14,
  persistSamples: false,
};

export const MIN_PORT_VALUE = 1;
export const MAX_PORT_VALUE = 65535;

export const MIN_FILTER_SIZE = 1;
export const MAX_FILTER_SIZE = 50;

export const MIN_DATA_RETENTION = 1;
export const MAX_DATA_RETENTION = 730;

export const DEFAULT_LEVEL = Level.MEDIUM;

// searchable keywords
export function getSandboxEndpointSensorSearchableKeywords(): {
  [type in SandboxEndpointSensorSearchableKeywords]: SearchableKeyword;
} {
  return {
    [SandboxEndpointSensorSearchableKeywords.SandboxAnalyzer]: {
      translatedLabel: translateFn('Sandbox Analyzer'),
      label: 'Sandbox Analyzer',
    },
    [SandboxEndpointSensorSearchableKeywords.EndpointSensor]: {
      translatedLabel: translateFn('Endpoint Sensor'),
      label: 'Endpoint Sensor',
    },
    [SandboxEndpointSensorSearchableKeywords.ConnectionSettings]: {
      translatedLabel: translateFn('Connection settings'),
      label: 'Connection settings',
    },
    [SandboxEndpointSensorSearchableKeywords.CloudSandboxAnalyzer]: {
      translatedLabel: translateFn('Use Cloud Sandbox Analyzer'),
      label: 'Use Cloud Sandbox Analyzer',
    },
    [SandboxEndpointSensorSearchableKeywords.LocalSandboxAnalyzer]: {
      translatedLabel: translateFn('Use local Sandbox Analyzer instance'),
      label: 'Use local Sandbox Analyzer instance',
    },
    [SandboxEndpointSensorSearchableKeywords.ProxyConfiguration]: {
      translatedLabel: translateFn('Use proxy configuration'),
      label: 'Use proxy configuration',
    },
    [SandboxEndpointSensorSearchableKeywords.AutomaticSubmission]: {
      translatedLabel: translateFn('Automatic sample submission from managed endpoints'),
      label: 'Automatic sample submission from managed endpoints',
    },
    [SandboxEndpointSensorSearchableKeywords.AnalysisMode]: {
      translatedLabel: translateFn('Analysis mode'),
      label: 'Analysis mode',
    },
    [SandboxEndpointSensorSearchableKeywords.Monitoring]: {
      translatedLabel: translateFn('Monitoring'),
      label: 'Monitoring',
    },
    [SandboxEndpointSensorSearchableKeywords.Blocking]: {
      translatedLabel: translateFn('Blocking'),
      label: 'Blocking',
    },
    [SandboxEndpointSensorSearchableKeywords.Remediation]: {
      translatedLabel: translateFn('Remediation Actions'),
      label: 'Remediation Actions',
    },
    [SandboxEndpointSensorSearchableKeywords.DefaultAction]: {
      translatedLabel: translateFn('Default action'),
      label: 'Default action',
    },
    [SandboxEndpointSensorSearchableKeywords.ContentPrefiltering]: {
      translatedLabel: translateFn('Content prefiltering'),
      label: 'Content prefiltering',
    },
    [SandboxEndpointSensorSearchableKeywords.Exclusions]: {
      translatedLabel: translateFn('Exclusions'),
      label: 'Exclusions',
    },
    [SandboxEndpointSensorSearchableKeywords.DoNotSubmit]: {
      translatedLabel: translateFn('Minimum size for submitted objects'),
      label: 'Minimum size for submitted objects',
    },
    [SandboxEndpointSensorSearchableKeywords.DetonationProfile]: {
      translatedLabel: translateFn('Detonation profile'),
      label: 'Detonation profile',
    },
  };
}

export function getSandboxNetworkSensorSearchableKeywords(): {
  [type in SandboxNetworkSensorSearchableKeywords]: SearchableKeyword;
} {
  return {
    [SandboxNetworkSensorSearchableKeywords.AutomaticSubmission]: {
      translatedLabel: translateFn('Automatic samples submission from network sensor'),
      label: 'Automatic samples submission from network sensor',
    },
    [SandboxNetworkSensorSearchableKeywords.ConnectionSettings]: {
      translatedLabel: translateFn('Connection settings'),
      label: 'Connection settings',
    },
    [SandboxNetworkSensorSearchableKeywords.ProxyConfiguration]: {
      translatedLabel: translateFn('Use proxy configuration'),
      label: 'Use proxy configuration',
    },
    [SandboxNetworkSensorSearchableKeywords.ContentPrefiltering]: {
      translatedLabel: translateFn('Content prefiltering'),
      label: 'Content prefiltering',
    },
    [SandboxNetworkSensorSearchableKeywords.Exclusions]: {
      translatedLabel: translateFn('Exclusions'),
      label: 'Exclusions',
    },
    [SandboxNetworkSensorSearchableKeywords.DoNotSubmit]: {
      translatedLabel: translateFn('Minimum size for submitted objects'),
      label: 'Minimum size for submitted objects',
    },
    [SandboxNetworkSensorSearchableKeywords.DetonationProfile]: {
      translatedLabel: translateFn('Detonation profile'),
      label: 'Detonation profile',
    },
  };
}

export function getSandboxIcapSensorSearchableKeywords(): {
  [type in SandboxIcapSensorSearchableKeywords]: SearchableKeyword;
} {
  return {
    [SandboxIcapSensorSearchableKeywords.AutomaticSubmission]: {
      translatedLabel: translateFn('Automatic samples submission from ICAP sensor'),
      label: 'Automatic samples submission from ICAP sensor',
    },
    [SandboxIcapSensorSearchableKeywords.ConnectionSettings]: {
      translatedLabel: translateFn('Connection settings'),
      label: 'Connection settings',
    },
    [SandboxIcapSensorSearchableKeywords.ProxyConfiguration]: {
      translatedLabel: translateFn('Use proxy configuration'),
      label: 'Use proxy configuration',
    },
    [SandboxIcapSensorSearchableKeywords.ContentPrefiltering]: {
      translatedLabel: translateFn('Content prefiltering'),
      label: 'Content prefiltering',
    },
    [SandboxIcapSensorSearchableKeywords.Exclusions]: {
      translatedLabel: translateFn('Exclusions'),
      label: 'Exclusions',
    },
    [SandboxIcapSensorSearchableKeywords.DoNotSubmit]: {
      translatedLabel: translateFn('Minimum size for submitted objects'),
      label: 'Minimum size for submitted objects',
    },
    [SandboxIcapSensorSearchableKeywords.DetonationProfile]: {
      translatedLabel: translateFn('Detonation profile'),
      label: 'Detonation profile',
    },
    [SandboxIcapSensorSearchableKeywords.SubmitFiles]: {
      translatedLabel: translateFn('Automatically submit files marked as malicious'),
      label: 'Automatically submit files marked as malicious',
    },
  };
}

export function getSandboxManagerSearchableKeywords(): {
  [type in SandboxManagerSearchableKeywords]: SearchableKeyword;
} {
  return {
    [SandboxManagerSearchableKeywords.SandboxManager]: {
      translatedLabel: translateFn('Sandbox Manager'),
      label: 'Sandbox Manager',
    },
    [SandboxManagerSearchableKeywords.PersistSamples]: {
      translatedLabel: translateFn('Allow Sandbox Analyzer to keep submitted objects'),
      label: 'Allow Sandbox Analyzer to keep submitted objects',
    },
    [SandboxManagerSearchableKeywords.DataRetention]: {
      translatedLabel: translateFn('Keep the report and submitted objects for (days)'),
      label: 'Keep report and submitted objects for (days)',
    },
    [SandboxManagerSearchableKeywords.AutomaticDelete]: {
      translatedLabel: translateFn(
        'Enter a number between 1 and 730. Sandbox Analyzer data older than the specified number of days will be automatically deleted.'
      ),
      label:
        'Enter a number between 1 and 730. Sandbox Analyzer data older than the specified number of days will be automatically deleted.',
    },
  };
}

export function getRemediationActionConfigsByAnalysisMode() {
  return {
    [AnalysisMode.MONITORING]: {
      defaultActions: {
        value: RemediationAction.REPORT_ONLY,
        options: [
          {
            value: RemediationAction.REMEDIATE,
            title: translateFn('Remediate'),
          },
          {
            value: RemediationAction.QUARANTINE,
            title: translateFn('Move to quarantine'),
          },
          {
            value: RemediationAction.REPORT_ONLY,
            title: translateFn('Report only'),
          },
        ],
      },
    },
    [AnalysisMode.BLOCKING]: {
      defaultActions: {
        value: RemediationAction.QUARANTINE,
        options: [
          {
            value: RemediationAction.QUARANTINE,
            title: translateFn('Move to quarantine'),
          },
          {
            value: RemediationAction.REPORT_ONLY,
            title: translateFn('Report only'),
          },
        ],
      },
    },
  };
}
